import { useEffect, useState, useCallback } from 'react';
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Clock,
  Cpu,
  Image as ImageIcon,
  Layers,
  Monitor,
  Palette,
  TrendingDown,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import { platformFetch } from '../../platform/http';
import type { GenerationStats } from './types';
import { addDaysToBeijingDateKey, formatBeijingDateKey } from '../../utils/time';

function formatDate(date: Date): string {
  return formatBeijingDateKey(date);
}

function formatDurationMinutes(durationMs: number): string {
  const minutes = durationMs / 60000;
  return `${minutes.toFixed(1)} 分钟`;
}

function OverviewCard({
  title,
  value,
  icon: Icon,
  color,
  suffix,
}: {
  title: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  suffix?: string;
}) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-lg border border-light-border">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-light-muted text-sm">{title}</p>
          <p className="text-2xl font-bold text-light-text mt-2 sm:text-3xl">
            {typeof value === 'number' ? value.toLocaleString() : value}
            {suffix ? <span className="text-lg ml-1">{suffix}</span> : null}
          </p>
        </div>
        <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
}

function RankList({
  title,
  icon: Icon,
  data,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  data: Array<{ label: string; count: number; total: number }>;
}) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-lg border border-light-border">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-light-muted" />
        <h3 className="font-semibold text-light-text">{title}</h3>
      </div>
      <div className="space-y-3">
        {data.map((item, index) => (
          <div key={item.label} className="flex items-center gap-3">
            <span
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                index === 0
                  ? 'bg-yellow-100 text-yellow-700'
                  : index === 1
                    ? 'bg-gray-100 text-gray-700'
                    : index === 2
                      ? 'bg-orange-100 text-orange-700'
                      : 'bg-gray-50 text-gray-500'
              }`}
            >
              {index + 1}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3 mb-1">
                <span className="text-sm text-light-text truncate">{item.label}</span>
                <span className="text-sm font-medium text-light-muted">{item.count}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className="bg-light-primary h-2 rounded-full transition-all"
                  style={{ width: `${item.total > 0 ? (item.count / item.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
        ))}
        {data.length === 0 && (
          <p className="text-sm text-light-muted text-center py-4">暂无数据</p>
        )}
      </div>
    </div>
  );
}

function ChannelTable({
  data,
}: {
  data: Array<{
    channelId: string;
    channelName: string;
    callCount: number;
    failRate: number;
    costPerCallLow: number;
    costPerCallMedium: number;
    costPerCallHigh: number;
    lowCallCount: number;
    mediumCallCount: number;
    highCallCount: number;
    lowTotalCost: number;
    mediumTotalCost: number;
    highTotalCost: number;
    totalCost: number;
  }>;
}) {
  const totalCost = data.reduce((sum, ch) => sum + ch.totalCost, 0);

  return (
    <div className="bg-white rounded-2xl p-5 shadow-lg border border-light-border">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Monitor className="w-5 h-5 text-light-muted" />
          <h3 className="font-semibold text-light-text">API 通道统计</h3>
        </div>
        <div className="text-right">
          <span className="text-sm text-light-muted">总成本</span>
          <p className="text-xl font-bold text-orange-600">¥{totalCost.toFixed(4)}</p>
        </div>
      </div>
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-light-border">
              <th className="text-left text-sm font-medium text-light-muted py-3 px-2">通道名称</th>
              <th className="text-right text-sm font-medium text-light-muted py-3 px-2">调用量</th>
              <th className="text-right text-sm font-medium text-light-muted py-3 px-2">失败率</th>
              <th className="text-right text-sm font-medium text-light-muted py-3 px-2">低清</th>
              <th className="text-right text-sm font-medium text-light-muted py-3 px-2">高清</th>
              <th className="text-right text-sm font-medium text-light-muted py-3 px-2">超清</th>
              <th className="text-right text-sm font-medium text-light-muted py-3 px-2">总成本</th>
              <th className="text-left text-sm font-medium text-light-muted py-3 px-2 pl-4">状态</th>
            </tr>
          </thead>
          <tbody>
            {data.map((channel) => (
              <tr key={channel.channelId} className="border-b border-light-border last:border-0">
                <td className="py-3 px-2 text-sm text-light-text">{channel.channelName}</td>
                <td className="py-3 px-2 text-sm text-light-text text-right">
                  {channel.callCount.toLocaleString()}
                </td>
                <td className="py-3 px-2 text-sm text-right">{channel.failRate}%</td>
                <td className="py-3 px-2 text-sm text-orange-600 text-right">
                  {channel.lowCallCount.toLocaleString()} 次 / ¥{channel.costPerCallLow.toFixed(4)} / ¥{channel.lowTotalCost.toFixed(4)}
                </td>
                <td className="py-3 px-2 text-sm text-orange-600 text-right">
                  {channel.mediumCallCount.toLocaleString()} 次 / ¥{channel.costPerCallMedium.toFixed(4)} / ¥{channel.mediumTotalCost.toFixed(4)}
                </td>
                <td className="py-3 px-2 text-sm text-orange-600 text-right">
                  {channel.highCallCount.toLocaleString()} 次 / ¥{channel.costPerCallHigh.toFixed(4)} / ¥{channel.highTotalCost.toFixed(4)}
                </td>
                <td className="py-3 px-2 text-sm font-medium text-orange-600 text-right">
                  ¥{channel.totalCost.toFixed(4)}
                </td>
                <td className="py-3 px-2 pl-4">
                  <FailRateBar failRate={channel.failRate} />
                </td>
              </tr>
            ))}
            {data.length === 0 && (
              <tr>
                <td colSpan={8} className="text-sm text-light-muted text-center py-4">
                  暂无数据
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="divide-y divide-light-border lg:hidden">
        {data.length === 0 ? (
          <div className="py-4 text-center text-sm text-light-muted">暂无数据</div>
        ) : (
          data.map((channel) => (
            <div key={channel.channelId} className="py-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-light-text">{channel.channelName}</div>
                  <div className="mt-1 text-xs text-light-muted">调用量 {channel.callCount.toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-light-muted">失败率</div>
                  <div className="text-sm font-medium text-light-text">{channel.failRate}%</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                <InfoCard
                  label="低清"
                  value={`${channel.lowCallCount.toLocaleString()} 次 / ¥${channel.costPerCallLow.toFixed(4)} / ¥${channel.lowTotalCost.toFixed(4)}`}
                />
                <InfoCard
                  label="高清"
                  value={`${channel.mediumCallCount.toLocaleString()} 次 / ¥${channel.costPerCallMedium.toFixed(4)} / ¥${channel.mediumTotalCost.toFixed(4)}`}
                />
                <InfoCard
                  label="超清"
                  value={`${channel.highCallCount.toLocaleString()} 次 / ¥${channel.costPerCallHigh.toFixed(4)} / ¥${channel.highTotalCost.toFixed(4)}`}
                />
                <InfoCard label="总成本" value={`¥${channel.totalCost.toFixed(4)}`} accent />
              </div>
              <div>
                <div className="mb-1 text-xs text-light-muted">稳定性</div>
                <FailRateBar failRate={channel.failRate} />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function FailRateBar({ failRate }: { failRate: number }) {
  return (
    <div className="w-full bg-gray-100 rounded-full h-2 max-w-[100px]">
      <div
        className={`h-2 rounded-full transition-all ${
          failRate > 20 ? 'bg-red-500' : failRate > 10 ? 'bg-yellow-500' : 'bg-green-500'
        }`}
        style={{ width: `${Math.min(failRate, 100)}%` }}
      />
    </div>
  );
}

function InfoCard({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="rounded-xl bg-light-hover p-3">
      <div className="text-xs text-light-muted">{label}</div>
      <div className={`mt-1 text-sm font-medium ${accent ? 'text-orange-600' : 'text-light-text'}`}>{value}</div>
    </div>
  );
}

export default function GenerationStatsTab() {
  const [stats, setStats] = useState<GenerationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentDate, setCurrentDate] = useState(() => formatDate(new Date()));

  const fetchStats = useCallback(async (date: string) => {
    try {
      setLoading(true);
      setError('');
      const response = await platformFetch(`/admin/generation-stats?date=${date}`);
      if (!response.ok) throw new Error('获取统计失败');
      const data = await response.json();
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取统计失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats(currentDate);
  }, [currentDate, fetchStats]);

  const handlePrevDay = () => {
    setCurrentDate(addDaysToBeijingDateKey(currentDate, -1));
  };

  const handleNextDay = () => {
    const today = formatBeijingDateKey();
    const next = addDaysToBeijingDateKey(currentDate, 1);
    if (next > today) return;
    setCurrentDate(next);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (!val) return;
    const today = formatDate(new Date());
    if (val > today) {
      setCurrentDate(today);
    } else {
      setCurrentDate(val);
    }
  };

  const isToday = currentDate === formatDate(new Date());

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-light-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={() => fetchStats(currentDate)}
          className="px-4 py-2 bg-light-primary text-white rounded-xl hover:bg-light-primary/90 transition-colors"
        >
          重试
        </button>
      </div>
    );
  }

  if (!stats) return null;

  const sizeTotal = stats.topSizes.reduce((s, i) => s + i.count, 0);
  const qualityTotal = stats.topQualities.reduce((s, i) => s + i.count, 0);
  const modelTotal = stats.topModels.reduce((s, i) => s + i.count, 0);
  const modeTotal = stats.topModes?.reduce((s, i) => s + i.count, 0) || 0;
  const userTotal = stats.topUsers.reduce((s, i) => s + i.count, 0);

  return (
    <div className="space-y-6">
      {/* 日期选择器 */}
      <div className="bg-white rounded-2xl p-4 shadow-lg border border-light-border">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-light-muted" />
            <h3 className="font-semibold text-light-text">生成数据统计</h3>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <button
              onClick={handlePrevDay}
              className="flex items-center justify-center gap-1 px-3 py-2 rounded-xl border border-light-border hover:bg-light-hover transition-colors text-sm text-light-text"
            >
              <ChevronLeft className="w-4 h-4" />
              前一天
            </button>
            <input
              type="date"
              value={currentDate}
              onChange={handleDateChange}
              max={formatDate(new Date())}
              className="w-full px-3 py-2 rounded-xl border border-light-border text-sm text-light-text focus:outline-none focus:ring-2 focus:ring-light-primary/20 sm:w-auto"
            />
            <button
              onClick={handleNextDay}
              disabled={isToday}
              className={`flex items-center justify-center gap-1 px-3 py-2 rounded-xl border border-light-border transition-colors text-sm ${
                isToday
                  ? 'text-light-muted cursor-not-allowed opacity-50'
                  : 'text-light-text hover:bg-light-hover'
              }`}
            >
              后一天
              <ChevronRight className="w-4 h-4" />
            </button>
            {isToday && (
              <span className="px-2 py-1 bg-accent-blue/10 text-accent-blue text-xs rounded-lg font-medium">
                今天
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 今日概览 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-6">
        <OverviewCard
          title="生成次数"
          value={stats.todayCount}
          icon={ImageIcon}
          color="bg-blue-500"
        />
        <OverviewCard
          title="消耗积分"
          value={stats.todayCredits}
          icon={Zap}
          color="bg-purple-500"
        />
        <OverviewCard
          title="成功率"
          value={stats.successRate}
          icon={TrendingUp}
          color="bg-green-500"
          suffix="%"
        />
        <OverviewCard
          title="失败率"
          value={stats.failureRate}
          icon={TrendingDown}
          color="bg-red-500"
          suffix="%"
        />
        <OverviewCard
          title="平均生成耗时"
          value={formatDurationMinutes(stats.avgDuration)}
          icon={Clock}
          color="bg-orange-500"
        />
      </div>

      {/* 排行统计 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RankList
          title="最常用尺寸 TOP5"
          icon={Layers}
          data={stats.topSizes.map((s) => ({ label: s.size, count: s.count, total: sizeTotal }))}
        />
        <RankList
          title="最常用质量 TOP3"
          icon={BarChart3}
          data={stats.topQualities.map((q) => ({
            label: q.quality,
            count: q.count,
            total: qualityTotal,
          }))}
        />
        <RankList
          title="最常用模型 TOP5"
          icon={Cpu}
          data={stats.topModels.map((m) => ({
            label: m.model,
            count: m.count,
            total: modelTotal,
          }))}
        />
        <RankList
          title="最常用模式"
          icon={Palette}
          data={(stats.topModes || []).map((m) => ({
            label: m.name,
            count: m.count,
            total: modeTotal,
          }))}
        />
        <RankList
          title="最活跃用户 TOP10"
          icon={Users}
          data={stats.topUsers.map((u) => ({
            label: u.email,
            count: u.count,
            total: userTotal,
          }))}
        />
      </div>

      {/* API 通道统计 */}
      <ChannelTable data={stats.channelStats} />
    </div>
  );
}
